import React, { Component } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation, TFunction } from 'react-i18next';
import './LeaveRequestHistoryPage.css';

// Redux actions
import GetListHistoryActions from '../../../redux/GetListHistoryRedux';

// Icons
import moment from 'moment';

interface LeaveRequestHistoryPageProps {
  navigate: NavigateFunction;
  t?: TFunction;
  // Redux props
  currentSettings: any;
  getListHistoryFetching: boolean;
  getListHistorySuccess: boolean;
  getListHistoryError: boolean;
  listHistory: any;
  // Redux actions
  getListHistory: (type_id: string, children_id: string, page: string) => void;
}

interface LeaveRequestHistoryPageState {
  dataLeave: any[];
  page: number;
  total: number | null;
  isInitialLoad: boolean;
}

class LeaveRequestHistoryPage extends Component<LeaveRequestHistoryPageProps, LeaveRequestHistoryPageState> {
  constructor(props: LeaveRequestHistoryPageProps) {
    super(props);
    
    // Get parameters from URL
    const type_id = this.getRouteParam('type_id');
    const children_id = this.getChildrenId();
    
    // Fetch history on component mount
    if (type_id && children_id) {
      this.props.getListHistory(type_id, children_id, '1');
    }
    
    this.state = {
      dataLeave: [],
      page: 1,
      total: null,
      isInitialLoad: true,
    };
  }

  componentDidMount() {
    console.log('LeaveRequestHistoryPage mounted');
  }

  componentDidUpdate(prevProps: LeaveRequestHistoryPageProps) {
    // Handle get list history success/error
    if (!this.props.getListHistoryFetching) {
      if (
        this.props.getListHistorySuccess &&
        this.props.getListHistorySuccess !== prevProps.getListHistorySuccess
      ) {
        // Mark initial load as complete
        this.setState({ isInitialLoad: false });
        
        // Update total count
        this.setState({
          total: this.props.listHistory?.meta?.total
        });
        
        // Handle pagination data
        if (this.state.page !== 1) {
          // Append new data for pagination
          this.setState({
            dataLeave: this.state.dataLeave.concat(this.props.listHistory?.data?.leave_data || [])
          });
        } else {
          // Replace data for initial load or refresh
          this.setState({
            dataLeave: this.props.listHistory?.data?.leave_data || []
          });
        }
      }
    }

    if (
      this.props.getListHistoryError &&
      this.props.getListHistoryError !== prevProps.getListHistoryError
    ) {
      // Mark initial load as complete even on error
      this.setState({ 
        isInitialLoad: false
      });
    }
  }

  getRouteParam(paramName: string): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(paramName);
  }

  getChildrenId(): string | null {
    // Try multiple sources for children_id
    let children_id = null;
    
    // 1. Try currentSettings.child.salesforce_contact_id
    if (this.props.currentSettings?.child?.salesforce_contact_id) {
      children_id = this.props.currentSettings.child.salesforce_contact_id;
    }
    
    // 2. Try currentSettings.child.id if salesforce_contact_id is not available
    else if (this.props.currentSettings?.child?.id) {
      children_id = this.props.currentSettings.child.id;
    }
    
    if (!children_id) {
      console.error('LeaveRequestHistoryPage: No valid children_id found');
    }
    
    return children_id;
  }

  _onRefresh = () => {
    const type_id = this.getRouteParam('type_id');
    const children_id = this.getChildrenId();
    
    if (type_id && children_id) {
      this.setState({ page: 1 });
      this.props.getListHistory(type_id, children_id, '1');
    }
  }

  loadData = () => {
    if (this.props.getListHistoryFetching) {
      return;
    }
    
    const type_id = this.getRouteParam('type_id');
    const children_id = this.getChildrenId();
    
    if (type_id && children_id && this.state.page <= (this.props.listHistory?.meta?.last_page || 0)) {
      this.setState({ page: this.state.page + 1 }, () => {
        this.props.getListHistory(type_id, children_id, this.state.page.toString());
      });
    }
  }

  renderFooter = () => {
    if (!this.props.getListHistoryFetching) {
      return null;
    }
    return (
      <div className="loading-footer">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  render() {
    const { dataLeave, total, isInitialLoad } = this.state;
    const { 
      getListHistoryFetching, 
      getListHistorySuccess, 
      listHistory 
    } = this.props;

    // Show main loader only for initial load
    const shouldShowMainLoader = isInitialLoad;
    
    // Show pagination loading footer when fetching more data (not initial load)
    const isPaginationLoading = getListHistoryFetching && !isInitialLoad;

    return (
      <div className="leaverequesthistory-page">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-inner">
            <div className="header-left">
              <button 
                className="header-button" 
                onClick={() => this.props.navigate(-1)}
              >
                <svg width="16" height="11" viewBox="0 0 16 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.79646 0.204319C6.07989 0.482194 6.07989 0.943465 5.80201 1.2269L2.42862 4.61141H15.2831C15.6777 4.61141 16 4.93374 16 5.33388C16 5.73402 15.6777 6.05635 15.2831 6.05635H2.42862L5.80757 9.44086C6.08545 9.72429 6.07989 10.18 5.80201 10.4634C5.51858 10.7413 5.06843 10.7413 4.78499 10.4579L0.205627 5.84517C0.144495 5.77848 0.0944773 5.70623 0.0555749 5.61731C0.0166725 5.52839 0 5.43391 0 5.33944C0 5.15048 0.0722476 4.97264 0.205627 4.8337L4.78499 0.220992C5.05731 -0.0679974 5.51303 -0.073555 5.79646 0.204319Z" fill="black"></path>
                </svg>
              </button>
              <h1 className="header-title">
                {String(this.props.t?.('leave-request-history:leave-request-history') || 'Leave Request History')}
              </h1>
            </div>
            <div className="header-right">
              {/* Empty for now */}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="page-content">
          {shouldShowMainLoader ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>{String(this.props.t?.('leaverequesthistory:loading') || 'Loading leave request history...')}</p>
            </div>
          ) : dataLeave && dataLeave.length > 0 ? (
            <>
              {/* Summary Text */}
              <div className="summary-section">
                <p className="summary-text">
                  {String(this.props.t?.('leave-request-history:summary') || 'Summary')}
                </p>
              </div>

              {/* Total Leave Remaining */}
              {/* <div className="total-leave-section">
                <p className="total-leave-text">
                  {String(t?.('leave-request-history:total-leave-remaining') || 'Total Leave Remaining')}{' '}
                  {getListHistorySuccess && !getListHistoryFetching
                    ? listHistory?.data?.total_leave_remain >= 0
                      ? listHistory?.data?.total_leave_remain
                      : 'N/A'
                    : null}
                </p>
              </div> */}

              {/* Leave History Text */}
              <div className="history-section">
                <p className="history-text">
                  {String(this.props.t?.('leave-request-history:leave-history') || 'Leave History')}
                </p>
              </div>

              {/* Leave Request List */}
              <div className="leave-history-list">
                {dataLeave.map((item: any, index: number) => (
                  <div key={index} className="leave-history-item">
                    <div className="leave-item-content">
                      <div className="leave-item-header">
                        <h3 className="leave-item-title">
                          {String(this.props.t?.('verify-request:leave-request-cancel') || 'Leave Request Cancel')} {total ? total - index : ''}
                        </h3>
                      </div>
                      
                      <div className="leave-item-details">
                        <div className="detail-row">
                          <span className="detail-text">
                            {String(this.props.t?.('appointment-summary-screen:summary.item.name') || 'Name')}{' '}
                            {item?.name ? item.name : ''}
                          </span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-text">
                            {String(this.props.t?.('appointment-summary-screen:summary.item.date') || 'Date')}{' '}
                            {item?.date ? moment(item?.date?.date).format('DD MMMM YYYY') : ''}
                          </span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-text">
                            {String(this.props.t?.('appointment-summary-screen:summary.item.time') || 'Time')}{' '}
                            {item?.time || ''}
                          </span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-text">
                            {String(this.props.t?.('select-leavere-quest:therapist') || 'Therapist')}{' '}
                            {item?.therapist || ''}
                          </span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-text">
                            {String(this.props.t?.('select-leavere-quest:service-type') || 'Service Type')}{' '}
                            {item?.service_type || ''}
                          </span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-text">
                            {String(this.props.t?.('select-leavere-quest:type-of-leave') || 'Type of Leave')}{' '}
                            {item?.type_of_leave == 1
                              ? String(this.props.t?.('select-leavere-quest:sick-leave') || 'Sick Leave')
                              : String(this.props.t?.('select-leavere-quest:other-leave') || 'Other Leave')
                            }
                          </span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-text">
                            {String(this.props.t?.('select-leavere-quest:sick-note') || 'Sick Note')}{' '}
                            {item?.sick_note || ''}
                          </span>
                        </div>
                        
                        <div className="detail-row">
                          <span className="detail-text">
                            {String(this.props.t?.('leave-request-history:make-up-date') || 'Make-up Date')}{' '}
                            {item?.exist_make_up_session_date && item.exist_make_up_session_date.length > 0
                              ? item.exist_make_up_session_date.map((makeUpItem: any, makeUpIndex: number) => (
                                  <span key={makeUpIndex} className="make-up-date">
                                    {makeUpItem?.date ? moment(makeUpItem?.date).format('DD MMMM YYYY') : ''}
                                    {makeUpIndex < item.exist_make_up_session_date.length - 1 ? ', ' : ''}
                                  </span>
                                ))
                              : 'N/A'
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Load More Button */}
              {this.state.page < (listHistory?.meta?.last_page || 0) && (
                <div className="load-more-control">
                  <button 
                    className="load-more-button comman-btn w-100"
                    onClick={this.loadData}
                    disabled={this.props.getListHistoryFetching}
                  >
                    {this.props.getListHistoryFetching ? this.props.t?.("verify-request:loading") || "Loading..." : this.props.t?.("verify-request:load-more") || "Load More"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-history">
              <p className="no-history-message">
                {String(this.props.t?.('leave-request-history:no-leave-request-found') || 'No Leave request found.')}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

// Redux mapping
const mapStateToProps = (state: any) => {
  return {
    currentSettings: state.currentSettings,
    getListHistoryFetching: state.getListHistory.fetching,
    getListHistorySuccess: state.getListHistory.success,
    getListHistoryError: state.getListHistory.error,
    listHistory: state.getListHistory.listHistory,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    getListHistory: (type_id: string, children_id: string, page: string) =>
      dispatch(GetListHistoryActions.getListHistoryRequest(type_id, children_id, page)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(LeaveRequestHistoryPage));
